import React, { useState, useCallback, useRef, FC } from 'react';
import {
  Button,
  Grid,
  makeStyles,
  Menu,
  MenuItem,
  TranslateOutlinedIcon,
  Typography,
} from '@rbx/ui';
import { Locale, useTranslation } from '@rbx/intl';
import { setCookie } from '@rbx/core';
import {
  availableDocsContentLocales,
  defaultLocale,
  LocaleLanguageMap,
} from '@modules/localization/constants/translationResourceConstants';
import DOCS_LOCALE_COOKIE from '@modules/localization/constants/localizationCookieConstants';
import DocsSiteEventType from '@modules/eventStream/enum/DocsSiteEventType';
import DocsSiteSource from '@modules/eventStream/enum/DocsSiteSource';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import useCurrentLocale from '@modules/localization/hooks/useCurrentLocale';
import { hasLocalePathWithSlashes } from '@modules/utilities/utils/path';
import { TestId } from '@modules/utilities/constants/testConstants';

const useLanguageMenuStyles = makeStyles()((theme) => ({
  betaLabel: {
    marginLeft: theme.spacing(1.5),
  },
  languageMenuButtonStyle: {
    marginRight: theme.spacing(1),
  },
  languageMenuButtonRevamp: {
    color: theme.palette.content.muted,
  },
  translatedIcon: {
    marginLeft: theme.spacing(1),
  },
  translatedIconRevamp: {
    marginRight: '8px',
  },
}));

export const replaceUrlLocale = ({
  url,
  defaultLocale: defaultLocaleParam,
  oldLocale,
  newLocale,
}: {
  url: string;
  defaultLocale: Locale;
  oldLocale: Locale;
  newLocale: Locale;
}): string => {
  // Prefer no locale in URL for default (English) because it's better optimized for SEO.
  // We want folks to share the SEO optimized URLs.
  if (newLocale === defaultLocaleParam) {
    return url.replace(`/docs/${oldLocale.toLowerCase()}/`, `/docs/`);
  }
  if (hasLocalePathWithSlashes(url)) {
    return url.replace(oldLocale.toLowerCase(), newLocale.toLowerCase());
  }
  return url.replace(`/docs/`, `/docs/${newLocale.toLowerCase()}/`);
};

export interface LanguageMenuProps {
  engineRevampEnabled?: boolean;
}

const LanguageMenu: FC<React.PropsWithChildren<LanguageMenuProps>> = ({
  engineRevampEnabled = false,
}) => {
  const { translate } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const { urlLocale: currentUrlLocale } = useCurrentLocale();
  const { classes } = useLanguageMenuStyles();

  const onClickLanguageMenuButton = useCallback(() => {
    setOpen(!open);
  }, [open]);

  const onCloseMenu = useCallback(() => {
    setOpen(false);
  }, []);

  const onClickNewLocale = useCallback(
    (targetLocale: Locale) => () => {
      const logLanguageChangeEvent = (from: Locale, to: Locale) => {
        unifiedLoggerClient.logClickEvent({
          eventName: DocsSiteEventType.LanguageChanged,
          parameters: {
            from,
            to,
            source: DocsSiteSource.LanguageMenu,
          },
        });
      };

      const navigateUrlToNewLocale = (newLocale: Locale) => {
        const currentUrl = window.location.href;
        const newUrl = replaceUrlLocale({
          url: currentUrl,
          defaultLocale,
          oldLocale: currentUrlLocale,
          newLocale,
        });
        const isLocaleInUrl = hasLocalePathWithSlashes(currentUrl);

        setTimeout(() => {
          onCloseMenu();
          if (isLocaleInUrl) {
            window.open(newUrl, '_self');
          } else {
            // if going from default locale to another locale,
            // replace in history for better back button behavior
            window.location.replace(newUrl);
          }
        }, 100);
      };

      logLanguageChangeEvent(currentUrlLocale, targetLocale);
      setCookie(DOCS_LOCALE_COOKIE.Name, targetLocale.toLowerCase(), DOCS_LOCALE_COOKIE.Options);
      navigateUrlToNewLocale(targetLocale);
    },
    [currentUrlLocale, onCloseMenu],
  );

  return (
    <React.Fragment>
      <Button
        className={
          engineRevampEnabled ? classes.languageMenuButtonRevamp : classes.languageMenuButtonStyle
        }
        color='primaryBrand'
        data-testid={TestId.LanguageMenuButton}
        id={TestId.LanguageMenuButton}
        onClick={onClickLanguageMenuButton}
        ref={buttonRef}
        size='small'
        variant='text'>
        {engineRevampEnabled ? (
          <React.Fragment>
            <TranslateOutlinedIcon className={classes.translatedIconRevamp} fontSize='inherit' />
            <Typography variant='body1' color='inherit'>
              {LocaleLanguageMap.get(currentUrlLocale)}
            </Typography>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Typography variant='body1' color='inherit'>
              {LocaleLanguageMap.get(currentUrlLocale)}
            </Typography>
            <TranslateOutlinedIcon className={classes.translatedIcon} fontSize='inherit' />
          </React.Fragment>
        )}
      </Button>
      {buttonRef.current && (
        <Menu
          anchorEl={buttonRef.current}
          open={open}
          onClose={onCloseMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          variant='menu'>
          <MenuItem disabled>
            <Grid container direction='row' alignItems='center'>
              <Grid item>
                {translate('Label.SelectLanguage')} - {translate('Label.Beta')}
              </Grid>
            </Grid>
          </MenuItem>
          {availableDocsContentLocales.map((locale) => {
            return (
              <MenuItem onClick={onClickNewLocale(locale)} key={locale} value={locale}>
                <Grid container direction='row' alignItems='center'>
                  <Grid item>{LocaleLanguageMap.get(locale)}</Grid>
                </Grid>
              </MenuItem>
            );
          })}
        </Menu>
      )}
    </React.Fragment>
  );
};

export default LanguageMenu;
